import { bearerHeaders } from '../../api';
import { apiFetch } from '../../appFetch';

// AS1c · REST mot apps/api/src/routes/moderation.ts och routes/support.ts.

export type TargetType = 'chat' | 'hunt_chat' | 'album' | 'hunt_media' | 'ad' | 'profile';
export type Reason = 'offensive' | 'harassment' | 'illegal' | 'spam' | 'other';
export const REASONS: { value: Reason; label: string; sub: string }[] = [
  { value: 'offensive', label: 'Stötande', sub: 'Grovt språk, våld eller annat olämpligt' },
  { value: 'harassment', label: 'Trakasserier', sub: 'Hot, mobbning eller påhopp på någon' },
  { value: 'illegal', label: 'Olagligt', sub: 'T.ex. tjuvjakt, olaglig vapenhandel' },
  { value: 'spam', label: 'Spam', sub: 'Reklam eller bedrägeri' },
  { value: 'other', label: 'Annat', sub: 'Beskriv nedan' },
];
export const REASON_LABEL = Object.fromEntries(REASONS.map(r => [r.value, r.label])) as Record<Reason, string>;

/** Det som rapporteras/blockeras. meetingId behövs för jaktchatt (id = meddelandets clientId). */
export interface ModTarget { type: TargetType; id: string; meetingId?: string | null }

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, { ...init, headers: { 'Content-Type': 'application/json', ...bearerHeaders(), ...init?.headers } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Något gick fel');
  return data as T;
}
const post = <T,>(path: string, body: unknown) => call<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });

export interface BlockedUser { email: string; name: string; createdAt: number }
export interface MyModerationStatus { suspended: boolean; suspendedReason: string | null; warning: { text: string; at: number } | null }

export const modApi = {
  report: (t: ModTarget, reason: Reason, details: string) =>
    post<{ ok: true }>('/api/moderation/reports', { targetType: t.type, targetId: t.id, meetingId: t.meetingId, reason, details }),
  block: (t: ModTarget) => post<{ ok: true; email: string; name: string | null }>('/api/moderation/blocks', { targetType: t.type, targetId: t.id, meetingId: t.meetingId }),
  unblock: (email: string) => call<{ ok: true }>(`/api/moderation/blocks/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  blocks: () => call<{ blocks: BlockedUser[] }>('/api/moderation/blocks').then(d => d.blocks),
  me: () => call<MyModerationStatus>('/api/moderation/me'),
  ackWarning: () => post<{ ok: true }>('/api/moderation/me/ack', {}),
  support: (t: { name?: string; email: string; subject?: string; message: string; website?: string }) => post<{ ok: true; id: string }>('/api/support', t),
};

// ── Admin ────────────────────────────────────────────────────────

export interface AdminReport {
  id: string; targetType: TargetType; targetId: string; targetEmail: string | null; targetName: string | null;
  markId: string | null; markName: string | null; reason: Reason; reasonLabel: string; details: string | null;
  reporterEmail: string; reporterName: string | null;
  snapshot: { text?: string | null; title?: string | null; imageUrl?: string | null; url?: string | null; mediaType?: string; name?: string | null; caption?: string | null; markName?: string; createdAt?: number };
  status: string; action: string | null; adminNote: string | null; resolvedBy: string | null; resolvedAt: number | null; createdAt: number;
  user: { reports: number; warnings: number; suspended: boolean } | null;
}
export interface SupportTicket {
  id: string; name: string | null; email: string; userEmail: string | null; subject: string; message: string;
  status: string; adminNote: string | null; closedBy: string | null; closedAt: number | null; createdAt: number;
}
export interface SuspendedUser { email: string; suspendedAt: number; reason: string | null; by: string | null; warnings: number }
export type AdminAction = 'dismiss' | 'remove' | 'warn' | 'suspend';

export const modAdminApi = {
  reports: (status: 'open' | 'closed') => call<{ reports: AdminReport[] }>(`/api/moderation/admin/reports?status=${status}`).then(d => d.reports),
  resolve: (id: string, body: { actions: AdminAction[]; warning?: string; suspendReason?: string; note?: string }) =>
    post<{ ok: true }>(`/api/moderation/admin/reports/${encodeURIComponent(id)}/resolve`, body),
  suspended: () => call<{ users: SuspendedUser[] }>('/api/moderation/admin/suspended').then(d => d.users),
  unsuspend: (email: string) => post<{ ok: true }>('/api/moderation/admin/unsuspend', { email }),
  tickets: (status: 'open' | 'closed') => call<{ tickets: SupportTicket[] }>(`/api/moderation/admin/tickets?status=${status}`).then(d => d.tickets),
  closeTicket: (id: string, note: string) => post<{ ok: true }>(`/api/moderation/admin/tickets/${encodeURIComponent(id)}/close`, { note }),
};

export const TARGET_LABEL: Record<TargetType, string> = {
  chat: 'Chattmeddelande', hunt_chat: 'Jaktchatt', album: 'Bild i album', hunt_media: 'Jaktbild', ad: 'Annons', profile: 'Profil',
};
